import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Tab, Tree } from "@/comn/components";
import { useForm, useFetch, useToast, useModal, useResource, useTree, usePage, TFormSchema } from "@/comn/hooks";
import { BASE, URLS, APIS, SF_ORG_USR_INFO } from "@/tra/tancis/ptli/usr/orgusr/services/PtliOrgUsrService";

/*
 * Organization User Management Detail
 * !== 기관 사용자 관리 상세 조회 ==!
 */
export const PTLI0917004Q = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { xtrnUsrUuid } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }, { area: "cntyCd" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization User Information Form !== 기관 사용자 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrInfo: useFetch({
            api: (data) => APIS.getOrgUsrInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Delete Organization User Information !== 기관 사용자 정보 삭제 ==!
        deleteOrgUsrInfo: useFetch({
            api: (xtrnUsrUuid) => APIS.deleteOrgUsrInfo(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(`${URLS.ptli0917001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        executeUsrAprv: useFetch({
            api: (data) => APIS.executeUsrAprv({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00066", // Successfully approved., !== 정상적으로 승인되었습니다. ==!
                });
                navigate(URLS.ptli0917001q);
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),

        executeUsrRtrn: useFetch({
            api: (data) => {
                console.log(data);
                return APIS.executeUsrRtrn(data);
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.00003",
                    onConfirm: () => {
                        modal.closeModal();
                    },
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),

        //  !== 기관 사용자 비밀번호를 초기화한다. ==!
        updateOrgUsrPwdInit: useFetch({
            api: (data) => APIS.updateOrgUsrPwdInit(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.ptl.00764", // A temporary password has been sent. !==  임시 비밀번호가 발송되었습니다. ==!
                });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete Organization User Information !== 기관 사용자 정보 삭제 ==!
        deleteOrgUsrInfo: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteOrgUsrInfo.fetch(xtrnUsrUuid);
                },
            });
        },

        // Execute Organization User Approval !== 기관 사용자 직원 승인 ==!
        executeUsrAprv: () => {
            if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgUsrInfo.handleSubmit(
                () => {
                    const data = form.orgUsrInfo.getValues();
                    data.aprvYn = "Y";
                    modal.openModal({
                        content: "msg.com.00053", // Confirm approve? !== 승인하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.executeUsrAprv.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Execute Company Employee Return !== 업체 직원 반려 ==!
        executeEmpRtrn: () => {
            if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgUsrInfo.handleSubmit(
                () => {
                    const handlePopup = (data: any) => {
                        const formData = form.orgUsrInfo.getValues();
                        formData.aprvYn = "N";

                        formData.rtrnRsn = data;

                        modal.closeModal();

                        fetch.executeUsrRtrn.fetch(formData);
                    };

                    modal.openModal({
                        content: <ModalRtrnRsn handlePopup={handlePopup} />,
                        size: "sm",
                        draggable: true,
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        onResetBtnClick: () => {
            modal.openModal({
                content: "msg.ptl.00749", // Do you want to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }
                    form.orgUsrInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.orgUsrInfo.getValues();

                            fetch.updateOrgUsrPwdInit.fetch(usrAplyInfo);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        },
                    )();
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrInfo.fetch();
        form.orgUsrInfo.setEditable(false);
    }, []);

    const isNew = form.orgUsrInfo.watch("isNew");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                            <Group.Control {...form.orgUsrInfo.schema.deptCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.frgnrYn} />
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrStatCd} />
                            <Group.Control {...form.orgUsrInfo.schema.orgRppnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.mdfyRsn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.cstmOfceCd} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(`${URLS.ptli0917001q}?menuId=${menuId}`);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right hidden={isNew === "NEW"}>
                    <Button
                        color="warning"
                        onClick={() => {
                            handler.onResetBtnClick();
                        }}
                    >
                        {t("B_RESET_PWD")}
                    </Button>

                    <Button
                        role="delete"
                        onClick={() => {
                            handler.deleteOrgUsrInfo();
                        }}
                    ></Button>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.ptli0917003s}/${xtrnUsrUuid}`);
                        }}
                    ></Button>
                </Layout.Right>

                <Layout.Right hidden={isNew !== "NEW"}>
                    <Layout.Button
                        color="blue"
                        onClick={() => {
                            handler.executeUsrAprv();
                        }}
                    >
                        {t("B_APRV")}
                    </Layout.Button>
                    <Layout.Button
                        color="error"
                        onClick={() => {
                            handler.executeEmpRtrn();
                        }}
                    >
                        {t("B_RTRN")}
                    </Layout.Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};

/*
 * !== 반려사유 Modal ==!
 */
export const ModalRtrnRsn = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "COM0024" }],
    });

    const { data, handlePopup } = props;

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            rtrnRsn: { type: "textarea", label: "L_RTRN_RSN", required: true, controlSize: 10, maxLength: 2000 },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    const handler = {
        submitBtnClick: () => {
            handlePopup(form.getValue("rtrnRsn"));
        },
    };

    return (
        <>
            <Page>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.rtrnRsn} />
                            </Group.Row>
                        </Group.Section>
                        <Group.Section>
                            <Layout>
                                <Layout.Right>
                                    <Button
                                        role={"submit"}
                                        onClick={() => {
                                            handler.submitBtnClick();
                                        }}
                                    />
                                </Layout.Right>
                            </Layout>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
